/**
 * VehicleDetail.js — Fonctions utilitaires pour la page de détail véhicule
 *
 * Exportées et utilisées dans VehicleDetail.jsx :
 *  - calculateTotalPrice  : calcule le prix total (base + options) selon les dates
 *  - getVehicleGallery    : construit la liste des images de la galerie
 *  - OPTION_PRICES        : tarifs journaliers des options (GPS, siège bébé, assurance)
 *
 * createGalleryPlaceholder et toUtcDate sont des helpers internes non exportés.
 */

// Prix journalier des options en DH
const OPTION_PRICES = {
	gps: 40,
	babySeat: 35,
	premiumInsurance: 120
};

/** Convertit une date "YYYY-MM-DD" en objet Date UTC (évite les décalages de fuseau horaire) */
function toUtcDate(dateValue) {
	if (!dateValue) return null;
	const date = new Date(`${dateValue}T00:00:00Z`);
	return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * calculateTotalPrice — Calcule le coût total d'une location.
 * La durée est arrondie au supérieur (Math.ceil) pour facturer toute journée commencée.
 * Les options sont facturées par jour (GPS, siège bébé, assurance premium).
 *
 * @param {number} pricePerDay  Prix de base par jour (DH)
 * @param {string} startDate    Date de début "YYYY-MM-DD"
 * @param {string} endDate      Date de fin "YYYY-MM-DD"
 * @param {object} options      { gps, babySeat, premiumInsurance } — true si activé
 * @returns {{ rentalDays, baseTotal, optionsTotal, total }}
 */
export function calculateTotalPrice(pricePerDay, startDate, endDate, options = {}) {
	const safePricePerDay = Number(pricePerDay) || 0;
	const start = toUtcDate(startDate);
	const end = toUtcDate(endDate);
	// Conversion ms → jours : 1000ms × 60s × 60min × 24h = 86 400 000 ms/jour
	const diffDays = start && end ? Math.ceil((end - start) / (1000 * 60 * 60 * 24)) : 1;
	const rentalDays = Math.max(1, diffDays || 1);

	const optionsPerDay = Object.entries(options).reduce((sum, [key, enabled]) => {
		if (!enabled) return sum;
		return sum + (OPTION_PRICES[key] || 0);
	}, 0);

	const optionsTotal = optionsPerDay * rentalDays;
	const baseTotal = safePricePerDay * rentalDays;

	return { rentalDays, baseTotal, optionsTotal, total: baseTotal + optionsTotal };
}

/**
 * getVehicleGallery — Construit la liste de toutes les images d'un véhicule.
 * Utilise un Set pour éviter les doublons si mainImageUrl apparaît aussi dans galleryImages.
 */
export function getVehicleGallery(vehicle) {
	if (!vehicle) return [];

	const seen = new Set();
	const images = [];

	const add = (url) => {
		if (url && !seen.has(url)) {
			seen.add(url);
			images.push(url);
		}
	};

	add(vehicle.image);
	for (const url of (vehicle.galleryImages || [])) {
		add(url);
	}

	return images.length > 0 ? images : [vehicle.image].filter(Boolean);
}

export { OPTION_PRICES };
